import type { UnknownOutputParams } from 'expo-router'
import type { ReactNode } from 'react'

import type { TEventOriginParam } from '@/src/general/constants/routes'
import { PUBLICATION_TRACKING_SOURCE, TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'

import type { TGetCommunityApiData } from '../../../api/getCommunity.types'
import type { useCommunitySubscriptionsQuery } from '../../../services/useCommunitySubscriptionsQuery'
import { usePublicationsOfCommunityQuery } from '../../../services/usePublicationsOfCommunityQuery'
import { CommunityActionButton } from './CommunityActionButton/CommunityActionButton'
import { CommunityScreenLayout } from './CommunityScreenLayout'

type TDefaultCommunityScreenProps = TEventOriginParam & {
  community: TGetCommunityApiData
  subscription: ReturnType<typeof useCommunitySubscriptionsQuery>['activeSubscription']
  action?: string
  searchParams: UnknownOutputParams
}

export const DefaultCommunityScreen = ({
  community,
  subscription,
  action,
  origin,
  searchParams,
}: TDefaultCommunityScreenProps): ReactNode => {
  const publications = usePublicationsOfCommunityQuery({ id: community.id, type: community.lifetime, enabled: true })

  const nextPublicationIndex =
    subscription?.nextPublication && publications.data
      ? publications.data.findIndex((p) => p.id === subscription.nextPublication!.id)
      : -1

  return (
    <CommunityScreenLayout
      id={community.id}
      action={action}
      searchParams={searchParams}
      community={community}
      subscription={subscription}
      listProps={{
        publications: publications.data,
        isPending: false,
        isFetchingNextPage: publications.isFetchingNextPage,
        hasNextPage: publications.hasNextPage,
        fetchNextPage: publications.fetchNextPage,
        autoScrollIndex: nextPublicationIndex,
      }}
    >
      <CommunityActionButton
        communityId={community.id}
        community={community}
        course="page"
        origin={origin}
        source={TRACKING_SOURCE.COMMUNITY_PAGE}
        publicationTrackingSource={PUBLICATION_TRACKING_SOURCE.COMMUNITY_READ_MORE}
      />
    </CommunityScreenLayout>
  )
}
